import { useTranslation } from 'react-i18next';
import type { ChatScope } from '@/api/chat/chatApi.types';
import type { ChatMessageWithSendStatus } from '@/hooks/useChat';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { formatIsoDateTimeToTimeOnly } from '@/utils/dateFormatting';
import { cn } from '@/lib/utils';
import { MediaAttachmentPreview } from '@/components/chat/MediaAttachmentPreview';

interface MessageBubbleProps {
  scope: ChatScope;
  message: ChatMessageWithSendStatus;
  isOwnMessage: boolean;
  onRetry: (message: ChatMessageWithSendStatus) => void;
}

export function MessageBubble({ scope, message, isOwnMessage, onRetry }: MessageBubbleProps) {
  const { t } = useTranslation('chat');

  return (
    <div className={cn('flex flex-col', isOwnMessage ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[80%] sm:max-w-sm rounded-2xl px-4 py-2.5 space-y-2',
          isOwnMessage
            ? 'bg-zooprimary text-zoowhite rounded-br-sm'
            : 'bg-zoowhite border border-zooborder text-zooindigo rounded-bl-sm'
        )}
      >
        {message.attachments.map(function renderAttachment(attachment) {
          return (
            <MediaAttachmentPreview
              key={attachment.id}
              scope={scope}
              attachment={attachment}
              isOwnMessage={isOwnMessage}
            />
          );
        })}
        {message.body === null || message.body.length === 0 ? null : (
          <p className="text-sm whitespace-pre-wrap break-words">{message.body}</p>
        )}
      </div>
      <div
        className={cn(
          'mt-1 flex items-center gap-2 text-[11px]',
          isOwnMessage ? 'text-zoomuted flex-row-reverse' : 'text-zoomuted'
        )}
      >
        <span>{formatIsoDateTimeToTimeOnly(message.createdAt)}</span>
        {message.sendStatus === 'sending' ? <LoadingSpinner className="w-3 h-3" /> : null}
        {message.sendStatus === 'failed' ? (
          <button
            type="button"
            onClick={function retry() {
              onRetry(message);
            }}
            className="flex items-center gap-1 text-zooerrortext font-semibold"
          >
            <Icon name="AlertCircle" className="text-xs" />
            {t('chat:composer.retry')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
